import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useData } from '../context/DataContext';
import { auth } from '../src/firebase';
import { db } from '../db'; // Import Dexie DB
import { UpgradePlanSelector } from './UpgradePlanSelector';

// Webhook gecikmesi/saat kayması için tampon süre - ödeme yapılıp Shopier
// webhook'u henüz işlenmemişken kullanıcının yanlışlıkla kilitlenmesini önler.
const GRACE_PERIOD_MS = 12 * 60 * 60 * 1000;
const AUTO_RECHECK_INTERVAL_MS = 20000;

export const SubscriptionLockout: React.FC = () => {
    const { t } = useTranslation();
    const { appSettings, refreshData } = useData();
    const sub = appSettings.subscription;

    // Plan/status ne olursa olsun: ödeme tarihi (+ tampon) geçtiyse kilitle
    const isExpired = !!sub && (new Date(sub.nextBillingDate).getTime() + GRACE_PERIOD_MS < Date.now());
    const shouldBlock = isExpired;

    // Kilit ekranı açıkken aboneliği periyodik olarak yeniden çek; webhook geç
    // işlenirse kullanıcı elle yenilemeden kilit otomatik kalkar.
    useEffect(() => {
        if (!shouldBlock) return;
        const interval = setInterval(() => {
            refreshData({ fromCloud: true });
        }, AUTO_RECHECK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [shouldBlock, refreshData]);

    if (!shouldBlock || !sub) return null;

    const handleLogout = async () => {
        try {
            await auth.signOut();
            localStorage.clear();
            // SURGICAL FIX: Don't wipe everything, just reset the subscription
            // so the app is forced to fetch fresh data, BUT keep isSetupComplete=true
            // so we don't get sent back to the Setup wizard.
            const appSettings = await db.settings.get('appSettings');
            if (appSettings && appSettings.value) {
                await db.settings.put({
                    ...appSettings,
                    value: {
                        ...appSettings.value,
                        // Reset subscription to null or a temporary safe state
                        subscription: null,
                        // CRITICAL: Ensure setup stays complete
                        isSetupComplete: true
                    }
                });
            } else {
                // Fallback if settings are missing entirely (shouldn't happen if they were blocked)
                await db.settings.put({
                    key: 'appSettings',
                    value: {
                        appName: 'KahveInn',
                        isSetupComplete: true, // Bypass setup
                        subscription: null     // Force fetch
                    }
                });
            }

            window.location.href = '/login';
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-4">
            {/* Sızdırmaz Kilit: Mobilde yatay/dikey taşmayı önlemek için max-h-[90dvh] (landscape için 85dvh) eklendi. Görünmez scrollbar ve padding optimizasyonları yapıldı. */}
            <div className="bg-background-card border border-border-color rounded-2xl shadow-2xl max-w-md w-full text-center animate-in zoom-in duration-300 max-h-[90dvh] landscape:max-md:max-h-[85dvh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] max-md:p-5 md:max-lg:p-6 lg:p-8 flex flex-col">
                
                <div className="bg-accent-red/20 rounded-full flex items-center justify-center mx-auto border border-accent-red/30 shrink-0 max-md:w-16 max-md:h-16 max-md:mb-4 lg:w-20 lg:h-20 lg:mb-6">
                    <span className="material-symbols-outlined text-accent-red max-md:text-3xl lg:text-4xl">lock_clock</span>
                </div>

                <h2 className="font-black text-fg tracking-tight max-md:text-xl max-md:mb-2 lg:text-2xl lg:mb-2 shrink-0">
                    {sub.plan === 'trial' ? t('subscription.trial_expired_title', 'Deneme Süresi Sona Erdi') : t('subscription.subscription_ended', 'Abonelik Sonlandı')}
                </h2>

                <p className="text-text-secondary font-medium leading-relaxed max-md:text-sm max-md:mb-5 lg:mb-8 shrink-0">
                    {t('subscription.lockout_message', 'Uygulamayı kullanmaya devam etmek için lütfen aboneliğinizi yenileyin veya bir lisans anahtarı girin.')}
                </p>

                <div className="max-md:space-y-2 lg:space-y-3 mt-auto shrink-0">
                    <UpgradePlanSelector currentPlan={sub.plan} />

                    <button
                        onClick={() => refreshData({ fromCloud: true })}
                        className="w-full max-md:py-3 lg:py-3.5 bg-fg/5 hover:bg-fg/10 text-fg max-md:text-sm lg:text-base font-bold rounded-xl border border-fg/10 transition-all flex items-center justify-center gap-2"
                    >
                        <span className="material-symbols-outlined max-md:text-[20px]">refresh</span>
                        {t('common.refresh', 'Kontrol Et')}
                    </button>

                    <button
                        onClick={handleLogout}
                        className="w-full max-md:py-3 lg:py-3.5 text-accent-red hover:bg-accent-red/10 max-md:text-sm lg:text-base font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                        <span className="material-symbols-outlined max-md:text-[20px]">logout</span>
                        {t('common.logout', 'Çıkış Yap')}
                    </button>

                    <div className="h-px bg-border-color max-md:my-3 lg:my-4"></div>

                    <div className="flex justify-between max-md:text-[10px] lg:text-xs text-text-secondary">
                        <div className="flex flex-col items-start gap-1">
                            <span>Cafe ID: <span className="font-mono text-fg/50">{localStorage.getItem('cafeId') || 'Local'}</span></span>
                            <span>Status: <span className={sub.status === 'active' ? 'text-green-400' : 'text-red-400'}>{sub.status}</span></span>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            <span>{new Date().toLocaleDateString()}</span>
                            <span>Due: {new Date(sub.nextBillingDate).toLocaleDateString()}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
